import React from 'react';

import Button from 'components/DesignSystem/Button';
import Card from 'components/DesignSystem/Card';

import useNiveisContingenciaAberto from 'hooks/Relatorios/Dessem/useNiveisContingenciaAberto';
import { segmentarItalicos } from 'helper/Relatorios/Dessem/niveisContingencia';
import { DessemNiveisContingencia, DessemNiveisContingenciaItem } from 'types/Relatorios/Dessem';

import './index.scss';

export interface NiveisContingenciaCardProps {
  /** Título, introdução e itens do quadro (hoje a constante `NIVEIS_CONTINGENCIA_DESSEM`). */
  conteudo: DessemNiveisContingencia;
  /** Prefixo dos `id` do botão (`-toggle`) e do corpo (`-corpo`), par do `aria-controls`. */
  id?: string;
}

/** O texto de um item, com os trechos de `italicos` em `<em>`. */
function TextoDoItem({ texto, italicos }: DessemNiveisContingenciaItem) {
  // A chave do `<em>` é a posição do trecho no texto: única mesmo quando o mesmo trecho se repete.
  type TrechoComInicio = { inicio: number; texto: string; italico: boolean };
  const trechos = segmentarItalicos(texto, italicos).reduce<TrechoComInicio[]>(
    (acc, trecho) => {
      const anterior = acc[acc.length - 1];
      const inicio = anterior ? anterior.inicio + anterior.texto.length : 0;
      return [...acc, { ...trecho, inicio }];
    },
    [],
  );
  return (
    <>
      {trechos.map((trecho) => (
        trecho.italico ? <em key={trecho.inicio}>{trecho.texto}</em> : trecho.texto
      ))}
    </>
  );
}

/**
 * Quadro fixo "Níveis de Contingência", logo abaixo do card de Premissas Gerais do DESSEM: o `Card`
 * do DS (mesmo visual do card de cima) com o botão Mostrar/Ocultar nas ações. Nasce fechado; a
 * escolha fica guardada por usuário (`useNiveisContingenciaAberto`). Só mostra o que recebe em
 * `conteudo`: nada de API, aprovação ou status.
 */
export default function NiveisContingenciaCard({
  conteudo, id = 'dessem-niveis-contingencia',
}: NiveisContingenciaCardProps) {
  const [aberto, alternar] = useNiveisContingenciaAberto();
  const idToggle = `${id}-toggle`;
  const idCorpo = `${id}-corpo`;
  const modificador = aberto ? '' : ' dessem-niveis-contingencia--fechado';
  const className = `dessem-niveis-contingencia${modificador}`;

  // O corpo fica sempre no DOM, escondido por `hidden`: o `aria-controls` do botão precisa apontar
  // para um elemento que existe. O botão é `<button>` nativo: Enter e Espaço já acionam o clique.
  return (
    <Card
      title={conteudo.titulo}
      className={className}
      actions={(
        <Button
          type="tertiary"
          small
          icon={aberto ? 'up_arrow' : 'down_arrow'}
          iconPlacement="trailing"
          id={idToggle}
          className="dessem-niveis-contingencia__toggle"
          aria-expanded={aberto}
          aria-controls={idCorpo}
          onClick={alternar}
        >
          {aberto ? 'Ocultar' : 'Mostrar'}
        </Button>
      )}
    >
      <div
        id={idCorpo}
        className="dessem-niveis-contingencia__corpo"
        role="region"
        aria-label={conteudo.titulo}
        hidden={!aberto}
      >
        <p>{conteudo.intro}</p>
        <ul>
          {conteudo.itens.map((item) => (
            <li key={item.texto}>
              <TextoDoItem texto={item.texto} italicos={item.italicos} />
            </li>
          ))}
        </ul>
      </div>
    </Card>
  );
}
